import { Autocomplete, AutocompleteOption, Button, FormHelperText, Input, ToggleButtonGroup } from '@healthinal/ui';
import type { FocusEvent } from 'react';
import type { Control, FieldErrors, UseFormGetFieldState, UseFormRegister } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { PreventiveTherapyDto } from '../../../../../api/generated/contact-tracing-controller/preventive-therapy-controller.ts';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { SingeLineFormGrid, SingleLineFormControl } from '../../../../../components/form/SingeLineFormComponents.tsx';
import { FieldMetaInfos } from '../../../../../hooks/useAutoSaveForm.ts';
import { getDateAfterInputValue } from '../../../../../utils/date.ts';

export interface PreventiveTherapyFormValues {
  referralDate: string;
  followUpDate: string;
  tbiTherapy: PreventiveTherapyDto['tbiTherapy'];
  therapyTypes: NonNullable<PreventiveTherapyDto['therapyTypes']>;
  therapyStartDate: string;
  therapyEndDate: string;
  completionReason: PreventiveTherapyDto['completionReason'];
  hinweis: string;
}

export function getPreventiveTherapyDefaultValues(data: PreventiveTherapyDto): PreventiveTherapyFormValues {
  return {
    referralDate: data.referralDate ?? '',
    followUpDate: data.followUpDate ?? '',
    tbiTherapy: data.tbiTherapy ?? null,
    therapyTypes: data.therapyTypes ?? [],
    therapyStartDate: data.therapyStartDate ?? '',
    therapyEndDate: data.therapyEndDate ?? '',
    completionReason: data.completionReason ?? null,
    hinweis: data.hinweis ?? '',
  };
}

export function mapPreventiveTherapyFormValuesToPayload(
  data: PreventiveTherapyFormValues,
  { id, patientId }: { id: string; patientId: string },
): PreventiveTherapyDto {
  return {
    id,
    patientId,
    referralDate: data.referralDate || undefined,
    followUpDate: data.followUpDate || undefined,
    tbiTherapy: data.tbiTherapy ?? undefined,
    therapyTypes: data.therapyTypes,
    therapyStartDate: data.therapyStartDate || undefined,
    therapyEndDate: data.therapyEndDate || undefined,
    completionReason: data.completionReason ?? undefined,
    hinweis: data.hinweis || undefined,
  };
}

export const therapyTypeOptions: {
  value: NonNullable<PreventiveTherapyDto['therapyTypes']>[number];
  label: string;
}[] = [
  { value: 'ISONIAZID', label: 'Isoniazid' },
  { value: 'RIFAMPICIN', label: 'Rifampizin' },
  { value: 'PYRAZINAMIDE', label: 'Pyrazinamid' },
  { value: 'ETHAMBUTOL', label: 'Ethambuthol' },
  { value: 'OTHER', label: 'Andere' },
];

export const completionReasonOptions: {
  value: NonNullable<PreventiveTherapyDto['completionReason']>;
  label: string;
}[] = [
  { value: 'COMPLETED', label: 'Abgeschlossen' },
  { value: 'DISCONTINUED', label: 'Abgebrochen' },
  { value: 'OTHER', label: 'Anderer' },
  { value: 'DECEASED', label: 'Verstorben' },
];

export const preventiveTherapyFieldMetaInfos: FieldMetaInfos<PreventiveTherapyFormValues> = {
  referralDate: { debounce: true },
  followUpDate: { debounce: true },
  tbiTherapy: { debounce: false },
  therapyTypes: { debounce: false },
  therapyStartDate: { debounce: true },
  therapyEndDate: { debounce: true },
  completionReason: { debounce: false },
  hinweis: { debounce: true },
};

interface PreventiveTherapyFormFieldsProps {
  registerWithSaveOnBlur: UseFormRegister<PreventiveTherapyFormValues>;
  control: Control<PreventiveTherapyFormValues>;
  getFieldState: UseFormGetFieldState<PreventiveTherapyFormValues>;
  handleSaveOnBlur: (onBlur: () => void) => void;
  errors: FieldErrors<PreventiveTherapyFormValues>;
}

export function PreventiveTherapyFormFields({
  registerWithSaveOnBlur,
  control,
  getFieldState,
  handleSaveOnBlur,
  errors,
}: PreventiveTherapyFormFieldsProps) {
  const therapyStartDateValue = useWatch({ control, name: 'therapyStartDate' });
  const therapyEndMinDate = getDateAfterInputValue(therapyStartDateValue);

  const handleFieldBlur = (onBlur: () => void) => (event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    handleSaveOnBlur(onBlur);
  };

  return (
    <SingeLineFormGrid>
      <SingleLineFormControl label={'Überweisungsdatum'} fieldState={getFieldState('referralDate')}>
        <GermanDateInput
          control={control}
          name="referralDate"
          rules={{
            required: false,
          }}
          onValueBlur={() => handleSaveOnBlur(() => undefined)}
        />
      </SingleLineFormControl>

      <SingleLineFormControl label={'Rückmeldungsdatum'} fieldState={getFieldState('followUpDate')}>
        <GermanDateInput
          control={control}
          name="followUpDate"
          rules={{
            required: false,
          }}
          onValueBlur={() => handleSaveOnBlur(() => undefined)}
        />
      </SingleLineFormControl>

      <SingleLineFormControl label={'TBI Therapie'} fieldState={getFieldState('tbiTherapy')}>
        <Controller
          name={'tbiTherapy'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleFieldBlur(onBlur)}>
              <Button value={'YES'} onClick={() => onChange('YES')}>
                Ja
              </Button>
              <Button value={'NO'} onClick={() => onChange('NO')}>
                Nein
              </Button>
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>

      <SingleLineFormControl label={'Therapieart'} fieldState={getFieldState('therapyTypes')}>
        <Controller
          name={'therapyTypes'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => {
            const selectedOptions = therapyTypeOptions.filter((option) => value.includes(option.value));

            return (
              <Autocomplete
                multiple
                options={therapyTypeOptions}
                value={selectedOptions}
                onChange={(_, selectedOptions) => {
                  onChange(selectedOptions.map((option) => option.value));
                }}
                onBlur={handleFieldBlur(onBlur)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                variant={'plain'}
                placeholder={'Bitte auswählen'}
                renderOption={(props, option) => (
                  <AutocompleteOption {...props} key={option.value}>
                    {option.label}
                  </AutocompleteOption>
                )}
              />
            );
          }}
        />
      </SingleLineFormControl>

      <SingleLineFormControl label={'Therapie Startdatum'} fieldState={getFieldState('therapyStartDate')}>
        <GermanDateInput
          control={control}
          name="therapyStartDate"
          rules={{
            required: false,
          }}
          onValueBlur={() => handleSaveOnBlur(() => undefined)}
        />
      </SingleLineFormControl>

      <SingleLineFormControl label={'Therapie Enddatum'} fieldState={getFieldState('therapyEndDate')}>
        <GermanDateInput
          control={control}
          name="therapyEndDate"
          rules={{
            required: false,
            ...(therapyEndMinDate ? { min: { value: therapyEndMinDate, message: 'range' } } : {}),
          }}
          min={therapyEndMinDate}
          onValueBlur={() => handleSaveOnBlur(() => undefined)}
        />
        {errors.therapyEndDate && <FormHelperText>Das Datum muss nach dem Therapie Startdatum liegen</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl label={'Abschlussgrund'} fieldState={getFieldState('completionReason')}>
        <Controller
          name={'completionReason'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => {
            const selectedOption = completionReasonOptions.find((option) => option.value === value) ?? null;

            return (
              <Autocomplete
                options={completionReasonOptions}
                value={selectedOption}
                onChange={(_, selectedOption) => {
                  onChange(selectedOption?.value ?? null);
                }}
                onBlur={handleFieldBlur(onBlur)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                variant={'plain'}
                placeholder={'Bitte auswählen'}
                renderOption={(props, option) => (
                  <AutocompleteOption {...props} key={option.value}>
                    {option.label}
                  </AutocompleteOption>
                )}
              />
            );
          }}
        />
      </SingleLineFormControl>

      <SingleLineFormControl label={'Bemerkungen'} fieldState={getFieldState('hinweis')}>
        <Input {...registerWithSaveOnBlur('hinweis')} variant={'plain'} />
      </SingleLineFormControl>
    </SingeLineFormGrid>
  );
}
